import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Settings } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface InsuranceRenewal {
  assetId: string;
  assetCode: string;
  assetName: string;
  renewalDate: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceValue?: number;
}

interface UpcomingInsuranceRenewalsCardProps {
  renewals?: InsuranceRenewal[];
}

export function UpcomingInsuranceRenewalsCard({ renewals = [] }: UpcomingInsuranceRenewalsCardProps): React.ReactElement {
  const navigate = useNavigate();

  // Format the renewal date for display
  const formatRenewalDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'do MMMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Use the renewals data from the API
  const upcomingRenewals = renewals.map(renewal => ({
    id: renewal.assetId,
    code: renewal.assetCode,
    name: renewal.assetName,
    renewalDate: formatRenewalDate(renewal.renewalDate),
    insuranceProvider: renewal.insuranceProvider,
    insuranceValue: renewal.insuranceValue,
  }));

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets?id=${assetId}&tab=supplier`);
  };

  return (
    <Card className="relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black">

      
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Upcoming Insurance Renewals</CardTitle>
            <CardDescription>Assets requiring insurance renewal in the next 30 days</CardDescription>
          </div>
          <div className="flex gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="rounded-full p-1.5 hover:bg-background/80 transition-colors">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Shows assets with insurance expiring within the next 30 days. Click on any asset to view details.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <button className="rounded-full p-1.5 hover:bg-background/80 transition-colors">
              <Settings className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative">
        {upcomingRenewals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No insurance renewals due in the next 30 days</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table className="w-full">
              <thead className="sticky top-0 bg-white dark:bg-black">
                <tr className="border-b">
                  <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Code</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Renewal Date</th>
                </tr>
              </thead>
              <tbody>
                {upcomingRenewals.map((asset) => (
                  <tr 
                    key={asset.id} 
                    className="border-b last:border-b-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="py-2.5 px-2">
                      <button
                        onClick={() => handleAssetClick(asset.id)}
                        className="text-sm font-medium text-primary hover:text-primary/80 hover:underline transition-colors cursor-pointer"
                        title={`View ${asset.name} insurance details`}
                      >
                        {asset.code}
                      </button>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className="text-sm text-foreground">
                        {asset.name}
                      </span>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className="text-sm text-muted-foreground">
                        {asset.renewalDate}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
